import {Page} from "../page"

export const campaigns: Page = {
    uri: 'learn/campaigns',
    icon: '🗺️',
    title: $localize`:@@page-campaigns-title:Campaigns`,
    seoTitle: $localize`:@@page-campaigns-seo-title:Tracking board game campaigns — Gloomhaven, legacy and RPG sessions`,
    summary: $localize`:@@page-campaigns-summary:Keep legacy games and RPG campaigns in one place — sessions, characters, world state and notes, so nobody has to remember what happened last time.`,
    markdownContent: [
        $localize`:@@page-campaigns-what-campaigns-are:Campaigns are for games that span multiple sessions — legacy games that change over time, RPGs with evolving characters, or any game where continuity matters. A campaign keeps everything together in one place: who's playing, what's happened, what's coming next, and how the world and characters are changing.

## What Campaigns Are

A campaign is a container for a long-running game experience. Instead of logging isolated plays, you:

- Track all sessions in one timeline
- Maintain persistent world and character state
- Manage who's involved and when they're playing next
- Store shared lore and personal character backstories
- Schedule upcoming sessions and events

![A campaign page with notes, members, upcoming events and the session log](/assets/screenshots/campaigns-campaign-page.webp)

It's like a campaign notebook, but digital and shareable.`,

        $localize`:@@page-campaigns-creating-a-campaign-2026-10-16:## Creating a Campaign

Campaigns are part of the [${'Player'} plan](/premium). A campaign that belongs to a club runs on the club's own plan (${'Club'} or ${'Business'}, which include it), not on yours.

Start simple:

### Open the campaign creation form
Tap the **+** button — the context actions button — and pick **Create Campaign**.

### Pick a Game
Select from the catalog or search for your game.

### Name Your Campaign
Something like "Our Gloomhaven Saga" or "The Lost Kingdom."

### Optional Details
- **Description** — A few lines about the campaign's premise or goal
- **Cover Image** — Visual for your campaign card

### Bind a Game Kit (Optional)
If the game has a [Game Kit](/learn/game-kits), link it to your campaign. The Kit's template defines structured fields for world state and character data — like "Day," "Party Level," "Current Location," etc. Without a Kit, the campaign just has a world notes field and a per-player notes field — free text you can use however you like.

### Scope to a Group (Optional)
If you're running this campaign for a gaming [group](/learn/groups) or club, scope it to that group. Group members see it on the group's page. A campaign you already run can be linked to a club you run later, from **Manage** on its page.`,

        $localize`:@@page-campaigns-expansions:## Expansions

If you play the campaign with some of the game's expansions, tap **Expansions** when creating the campaign and pick them, or change them later from **Manage** on its page. The campaign page lists them under the game.

Every session you then log or start from the campaign comes with those expansions already ticked, so nobody has to add them each time. A single session can still drop one or add another: it changes that session only, not the campaign.`,

        $localize`:@@page-campaigns-campaign-lifecycle:## Campaign Lifecycle

Campaigns have three states:

### Active

Your campaign is ongoing. Full editing — change the world, update characters, log sessions, reschedule events, add or remove players. Everything is mutable.

### Completed
Campaign is done. Data is preserved and readable, but edits are restricted. You can still view character notes, review the timeline, and reminisce. Prevents accidental changes to a finished story.

### Archived
Campaign is hidden from your main lists, but not deleted. You can reactivate it to Active if you want to revisit or continue the story.

**Progression Rule**: Active → Completed or Archived. Completed → Active or Archived. Archived → Active. You can go back anytime.

Owners change the state from **Manage** on the campaign page: **Finish campaign**, **Archive** and **Reopen**. Saving the end-of-campaign score sheet can mark it completed in the same step.`,

        $localize`:@@page-campaigns-console:## Running a Campaign

Whoever runs a campaign runs it from its page, the same one its players use. Owners see the campaign's state and a **Manage** button at the top.

### Players
**Add member**, the icon beside **Members**, brings in Ludoya users and your own players without an account (the people you already log plays with), so a friend who never signed up still has a character and a seat. A member's ⋮ menu edits their character, benches a player who stopped coming (**Run sessions only**: new sessions no longer seat them), makes them an owner or removes them.

### Requests
When **Who can join the campaign** is **Request to join**, the people asking show above the members, with how long ago they asked and the message they sent; the top of the page says how many are waiting. **Accept** adds them to the campaign and saves them a seat at the upcoming sessions.

### Standings
With two or more players, **Standings** sum up every session: how many each player sat in, how many they won (not in a co-op game, where everyone wins together) and the points they scored.

### Final Score
When the campaign's Game Kit has an end-of-campaign score sheet, **Score end of campaign** fills it in right on the page. While the campaign runs the best total reads as **Leading**; save it with **Mark the campaign as completed** ticked and the winners show at the top.

### Manage
**Manage** opens the campaign's settings: name, picture, description, duration, expansions and Game Kit; who sees the campaign and who can join it and its sessions; the club it runs under; finishing, archiving or reopening it; and deleting it.`,

        $localize`:@@page-campaigns-members-and-roles:## Members and Roles

### Who Can Join
You decide how people get in, with **Who can join the campaign**:

- **Anyone can join** — a Join button on the campaign page, no approval needed
- **Request to join** — players ask (with an optional message) and you accept or reject them on the campaign page, where they show above the members
- **Invite only** — nobody joins on their own; you add members yourself

There's a separate **Who can join its sessions** setting for the individual game sessions, so you can keep the campaign roster tight while letting others sit in on a night, or the reverse. New campaigns start private, with **Request to join** and sessions for **Members only**: requests start coming once more people can see the campaign. Change any of the three when you create it or later from **Manage**.

Owners can also add players directly, with **Add member** beside **Members**: Ludoya users, and their own players who have no account. A group account can be a member too. Members can leave whenever they like.

![The member list: owners, a member, a GM marked "Not playing", and each player's colour](/assets/screenshots/campaigns-members.webp)

### Promote to Owner
Tap a member and choose **Promote to owner** to give them full editing rights. A campaign can have as many owners as it needs, all equal — this is how you run one with a co-GM.

### Demote to Member
**Demote to member** takes those rights away again. There are only two levels here: owner and member.

![A member's menu with character notes, demote, and remove actions](/assets/screenshots/campaigns-member-menu.webp)

### Member Permissions
Any member can:
- Edit their own character data
- Edit the campaign's **World State** and shared **Notes** — this is a shared record, not the GM's private one
- Log play sessions and schedule events
- Comment on the campaign

**Owners only:** removing members, promoting and demoting, and deleting the campaign.

### Running, Not Playing
Game masters who don't take a seat can mark themselves **not playing** — their card shows a "Not playing" badge and they're left out of session rosters, while still running everything.`,

        $localize`:@@page-campaigns-sessions-and-events:## Sessions and Events

![A campaign's session log, each session with its own title, date and length](/assets/screenshots/campaign-sessions.webp)

Every campaign page lists its sessions in two sections, one after the other:

### Upcoming
Planned future sessions show up at the top. Schedule your next session by creating an [event](/learn/events) tied to the campaign — pick a date, and it appears here so everyone knows when to show up. Upcoming sessions also sync with personal calendars like any other event.

### Sessions
Below the upcoming list, you'll find every session you've **[logged](/learn/play-logging)** against the campaign, from most recent to oldest. Already logged some plays before starting the campaign? Tap the link button next to **Sessions** and tick the ones that belong to it. Each entry links back to the underlying play with its full details: game, players, scores, photos, and any notes captured during the session. Use a [Game Kit](/learn/game-kits)'s structured session format for richer tracking, or just log a plain play — either way it anchors to the campaign's history.

### Naming Each Session
When you log a play into a campaign, the form asks which **scenario or chapter** it was: that is how the session reads in the list ("Session 12 · A Town in the Frost") instead of a bare number, and it shows beside the campaign's name on the play itself. It suggests the names the campaign used before, so the group keeps one style and a retried scenario keeps its name.

Forgot to name one? Edit the play and fill in its scenario or chapter.

### The Party's Chat
Every campaign comes with a **members chat** right under its member list: the place to argue about the loot between sessions. Members join it when they join the campaign and leave it when they leave. See [Messaging](/learn/messaging).

### Campaigns Run by a Club
A club sees all its campaigns in its [console](/learn/club-console): members and sessions for each, the ones nobody has played in a while grouped apart to close or archive in bulk, and every request to join in one queue.`,

        $localize`:@@page-campaigns-world-and-character-notes:## World and Character Notes

Every campaign has two notes fields that carry the state of your story between sessions.

### World Notes
A shared field for the persistent condition of the game world — everyone in the campaign reads it and can edit it, though the GM usually keeps it. Use it for things like:

- Session recaps — what happened last time
- Lore entries — important world details
- House rules — custom mechanics
- Major timeline events

**With a [Game Kit](/learn/game-kits) bound**, the Kit's template adds structured world fields on top of the notes, like "Current Day," "Kingdom Stability," or "Active Threat" — so you can track quantitative world state at a glance instead of digging through free text.

### Character Notes
Each participant has their own character notes field. Use it for:

- Backstory and motivations
- Inventory and gear
- Personal goals

You write your own notes, right under your card on the campaign page, and campaign owners can edit anyone's there too. **Everyone in the campaign can read them** — they appear on your member card for the whole table to see, so treat them as shared lore rather than a private diary. Keep genuine secrets between you and your GM somewhere else.

**With a [Game Kit](/learn/game-kits) bound**, the Kit adds structured character fields like "HP," "Experience Points," "Class," or "Equipment Loadout" — great for crunchier RPGs where you want discrete stats alongside the prose.`,

        $localize`:@@page-campaigns-field-types-with-game-kit-template:## Field Types (with Game Kit Template)

When you bind a Game Kit to your campaign, you inherit its field definitions:

- **Text** — Notes, descriptions, open-ended entries
- **Number** — Quantities, experience points, resources, health
- **Toggle** — Binary yes/no state (e.g., "Has Secret Artifact")
- **Selection** — Dropdown choices (e.g., Class = Fighter/Mage/Rogue)
- **Image** — Character portraits, location maps, or item illustrations
- **Role** — Link to roles defined in the Kit (e.g., "Character Class" = predefined classes)
- **Color** — Assign a color to a faction or team`,

        $localize`:@@page-campaigns-visibility:## Visibility

Campaign preview cards appear in two places:

### User Profiles
New campaigns start **private** — only invited members see them. Open one up by changing its visibility, and it appears on your [profile](/learn/user-profiles) for others to find. You can share any campaign as a link, image, or QR code, and campaigns you make visible collect likes and comments like anything else on Ludoya.

### Group Pages
If a campaign is scoped to a group, it appears on the group's page so members can see active and completed campaigns.`,

        $localize`:@@page-campaigns-related-pages:## Related Pages

- [Game Kits](/learn/game-kits) — Game Kits power structured campaign fields and templates
- [Events](/learn/events) — Schedule and organize campaign sessions
- [Play Logging](/learn/play-logging) — Log individual campaign plays to the timeline
- [Groups](/learn/groups) — Scope campaigns to groups for shared management`,
    ].join('\n\n---\n\n')
}
